import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Download, FileUp, TriangleAlert, Upload } from 'lucide-react'
import { formatMoney, formatNumber, type ImportOptions, type ImportPreview } from '@najah/core'
import {
  Alert, Badge, Button, Dialog, Switch,
  Table, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { api, ApiError, getAccessTokenForDownload } from '../lib/api'
import { fetchAndSave } from '../lib/download'

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'

/** يحوّل الملف إلى base64 بلا تحميل ضخم في الذاكرة دفعة واحدة */
async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

export function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [encoded, setEncoded] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [options, setOptions] = useState<ImportOptions>({
    createCategories: true,
    updateExisting: true,
    importQuantities: true,
  })

  function reset() {
    setFile(null)
    setEncoded(null)
    setPreview(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const previewMutation = useMutation({
    mutationFn: async (payload: { file: string; options: ImportOptions }) =>
      api<ImportPreview>('/import/preview', { method: 'POST', body: payload }),
    onSuccess: setPreview,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'تعذّرت قراءة الملف'),
  })

  const commitMutation = useMutation({
    mutationFn: async () =>
      api<{ created: number; updated: number; skipped: number; stockApplied: number }>('/import/commit', {
        method: 'POST',
        body: { file: encoded, options },
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      toast.success(
        'اكتمل الاستيراد',
        `أُنشئ ${formatNumber(res.created)} · حُدّث ${formatNumber(res.updated)}${res.skipped ? ` · تُخطّي ${formatNumber(res.skipped)}` : ''}`,
      )
      reset()
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'تعذّر الاستيراد'),
  })

  async function handleFile(selected: File | undefined) {
    if (!selected) return
    setError(null)
    setPreview(null)
    setFile(selected)
    const base64 = await fileToBase64(selected)
    setEncoded(base64)
    previewMutation.mutate({ file: base64, options })
  }

  async function downloadTemplate() {
    await fetchAndSave(`${BASE_URL}/api/import/template`, getAccessTokenForDownload(), 'najah-import-template.csv')
  }

  const errorRows = preview?.rows.filter((r) => r.action === 'error') ?? []
  const warnRows = preview?.rows.filter((r) => r.action !== 'error' && r.warnings.length > 0) ?? []
  const canCommit = !!preview && preview.missingRequired.length === 0 && preview.toCreate + preview.toUpdate > 0

  return (
    <Dialog
      open={open}
      onClose={() => { reset(); onClose() }}
      size="xl"
      title="استيراد المنتجات من ملف"
      description="ملف CSV مُصدَّر من Excel — الأعمدة تُتعرَّف تلقائياً بالعربية أو الفرنسية"
      dismissible={!commitMutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose() }} disabled={commitMutation.isPending}>
            إلغاء
          </Button>
          <Button
            onClick={() => commitMutation.mutate()}
            loading={commitMutation.isPending}
            disabled={!canCommit}
          >
            {preview
              ? `استيراد ${formatNumber(preview.toCreate + preview.toUpdate)} صنفاً`
              : 'استيراد'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && <Alert tone="danger">{error}</Alert>}

        {/* ── اختيار الملف ── */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={(e) => void handleFile(e.target.files?.[0])}
            className="hidden"
            id="import-file"
          />
          <Button
            variant="secondary"
            onClick={() => inputRef.current?.click()}
            startIcon={<FileUp className="size-4" />}
            loading={previewMutation.isPending}
          >
            {file ? 'ملف آخر' : 'اختر ملفاً'}
          </Button>
          {file && <span className="text-sm text-fg-muted">{file.name}</span>}
          <Button
            variant="ghost"
            onClick={() => void downloadTemplate()}
            startIcon={<Download className="size-4" />}
            className="ms-auto"
          >
            نموذج جاهز
          </Button>
        </div>

        {/* ── الخيارات ── */}
        <div className="grid gap-3 rounded-xl border border-border bg-bg-subtle p-4 sm:grid-cols-3">
          <Switch
            checked={options.updateExisting}
            onChange={(e) => setOptions((o) => ({ ...o, updateExisting: e.target.checked }))}
            label="تحديث الموجود"
            description="يُطابَق بالرمز ثم الباركود"
          />
          <Switch
            checked={options.createCategories}
            onChange={(e) => setOptions((o) => ({ ...o, createCategories: e.target.checked }))}
            label="إنشاء التصنيفات"
            description="التصنيفات غير الموجودة تُنشأ"
          />
          <Switch
            checked={options.importQuantities}
            onChange={(e) => setOptions((o) => ({ ...o, importQuantities: e.target.checked }))}
            label="استيراد الكميات"
            description="كرصيد افتتاحي في السجل"
          />
        </div>

        {preview && (
          <>
            {preview.missingRequired.length > 0 && (
              <Alert tone="danger" title="الملف ينقصه عمود أساسي">
                لا بدّ من عمود <span className="font-semibold">الاسم</span> وعمود{' '}
                <span className="font-semibold">سعر البيع</span>. نزّل النموذج الجاهز لترى الشكل المتوقّع.
              </Alert>
            )}

            {/* ── الملخّص ── */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: 'إجمالي الصفوف', value: preview.totalRows, tone: 'neutral' as const },
                { label: 'سيُنشأ', value: preview.toCreate, tone: 'success' as const },
                { label: 'سيُحدَّث', value: preview.toUpdate, tone: 'info' as const },
                { label: 'أخطاء', value: preview.errorCount, tone: 'danger' as const },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-border bg-surface px-4 py-3">
                  <p className="text-xs text-fg-subtle">{s.label}</p>
                  <p
                    className={cn(
                      'mt-0.5 text-xl font-bold',
                      s.tone === 'success' && s.value > 0 && 'text-success',
                      s.tone === 'danger' && s.value > 0 && 'text-danger',
                      s.tone === 'info' && s.value > 0 && 'text-info',
                    )}
                    dir="ltr"
                  >
                    {formatNumber(s.value)}
                  </p>
                </div>
              ))}
            </div>

            {preview.newCategories.length > 0 && (
              <Alert tone="info" title={`${formatNumber(preview.newCategories.length)} تصنيفاً جديداً سيُنشأ`}>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {preview.newCategories.slice(0, 12).map((c) => (
                    <Badge key={c} tone="neutral">{c}</Badge>
                  ))}
                  {preview.newCategories.length > 12 && (
                    <Badge tone="neutral">+{formatNumber(preview.newCategories.length - 12)}</Badge>
                  )}
                </div>
              </Alert>
            )}

            {preview.unmappedHeaders.length > 0 && (
              <Alert tone="warning" title="أعمدة لم يتعرّف عليها النظام — ستُهمَل">
                {preview.unmappedHeaders.join('، ')}
              </Alert>
            )}

            {/* ── الأخطاء ── */}
            {errorRows.length > 0 && (
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-danger">
                  <AlertTriangle className="size-4" />
                  صفوف ستُتخطّى ({formatNumber(errorRows.length)})
                </h3>
                <TableWrapper className="max-h-56 overflow-y-auto">
                  <Table>
                    <Thead>
                      <tr>
                        <Th>السطر</Th>
                        <Th>السبب</Th>
                      </tr>
                    </Thead>
                    <Tbody>
                      {errorRows.slice(0, 50).map((r) => (
                        <Tr key={r.line}>
                          <Td className="tabular-nums text-fg-muted" dir="ltr">{formatNumber(r.line)}</Td>
                          <Td className="text-danger">{r.errors.join(' · ')}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableWrapper>
                {errorRows.length > 50 && (
                  <p className="mt-1 text-xs text-fg-subtle">
                    وأكثر… صحّح الملف وأعد الرفع لرؤية البقيّة.
                  </p>
                )}
              </div>
            )}

            {warnRows.length > 0 && (
              <Alert tone="warning" title={`${formatNumber(warnRows.length)} صفاً بتحذير`}>
                <span className="inline-flex items-center gap-1.5">
                  <TriangleAlert className="size-3.5" />
                  {warnRows[0]?.warnings[0]} — تُستورَد على أي حال.
                </span>
              </Alert>
            )}

            {/* ── معاينة ما سيُكتب ── */}
            {preview.toCreate + preview.toUpdate > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-fg-muted">معاينة أول الأصناف</h3>
                <TableWrapper className="max-h-64 overflow-y-auto">
                  <Table>
                    <Thead>
                      <tr>
                        <Th>الإجراء</Th>
                        <Th>المنتج</Th>
                        <Th>التصنيف</Th>
                        <Th align="end">التكلفة</Th>
                        <Th align="end">البيع</Th>
                        <Th align="center">الكمية</Th>
                      </tr>
                    </Thead>
                    <Tbody>
                      {preview.rows
                        .filter((r) => r.action !== 'error')
                        .slice(0, 100)
                        .map((r) => (
                          <Tr key={r.line}>
                            <Td>
                              <Badge tone={r.action === 'create' ? 'success' : 'info'}>
                                {r.action === 'create' ? 'جديد' : 'تحديث'}
                              </Badge>
                            </Td>
                            <Td>
                              <p className="truncate font-medium text-fg">{r.data?.nameAr}</p>
                              <p className="font-mono text-xs text-fg-subtle" dir="ltr">{r.data?.sku}</p>
                            </Td>
                            <Td className="text-fg-muted">{r.data?.categoryName ?? '—'}</Td>
                            <Td align="end" className="tabular-nums" dir="ltr">
                              {formatMoney(r.data?.costPrice ?? 0)}
                            </Td>
                            <Td align="end" className="font-semibold tabular-nums" dir="ltr">
                              {formatMoney(r.data?.retailPrice ?? 0)}
                            </Td>
                            <Td align="center" className="tabular-nums" dir="ltr">
                              {formatNumber(r.data?.quantity ?? 0)}
                            </Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </TableWrapper>
              </div>
            )}
          </>
        )}

        {!preview && !previewMutation.isPending && (
          <div className="rounded-xl border border-dashed border-border px-6 py-10 text-center">
            <Upload className="mx-auto size-10 text-fg-subtle" />
            <p className="mt-3 text-sm font-medium text-fg">اختر ملف CSV لبدء المعاينة</p>
            <p className="mt-1 text-xs text-fg-subtle">
              لن يُكتب شيء قبل مراجعتك للمعاينة والضغط على «استيراد»
            </p>
          </div>
        )}
      </div>
    </Dialog>
  )
}
